import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { type FC, useCallback, useContext, useEffect, useState } from 'react';
import { Badge } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import { Eye, PencilSquare, ToggleOff, ToggleOn, Trash } from 'react-bootstrap-icons';
import { config } from '../../../modules/config';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { useNotifications } from '../../../modules/hooks';
import type { GuestThemeRecord } from '../../../modules/interfaces';
import { Can } from '../../../modules/utils/can';
import { FONT_OPTIONS } from '../../../modules/utils/guestTheme';
import { CanMulti } from '../../CanMulti';
import { DotDropDown } from '../../Customize/DropDown';
import { useGuestPortalThemes } from '../../Dashboards/GuestPortalThemes/useGuestPortalThemes';
import { ConfirmModal, type ModalState } from '../../Modals/Confirm';
import {
  GuestPortalThemeModal,
  type GuestPortalThemeModalMode,
} from '../../Modals/GuestPortalTheme';
import { DataTable } from '../DataTable';

const THEME_SUBJECT = 'guest_portal_themes';

const fontLabel = (value: string): string =>
  FONT_OPTIONS.find((option) => option.value === value)?.label ?? value;

interface StatusCellProps {
  data?: GuestThemeRecord;
}

const StatusCell: FC<StatusCellProps> = ({ data }) => {
  if (!data) return null;
  return (
    <Badge className="status-badge" bg={data.isActive ? 'primary' : 'danger'}>
      {data.isActive ? 'Enabled' : 'Disabled'}
    </Badge>
  );
};

interface ThemeDotMenuProps {
  data?: GuestThemeRecord;
  onView: (record: GuestThemeRecord) => void;
  onEdit: (record: GuestThemeRecord) => void;
  onToggleActive: (record: GuestThemeRecord) => void;
  onDelete: (record: GuestThemeRecord) => void;
}

const ThemeDotMenu: FC<ThemeDotMenuProps> = ({
  data,
  onView,
  onEdit,
  onToggleActive,
  onDelete,
}) => {
  if (!data) return null;
  return (
    <CanMulti actions={['update', 'delete']} subject={THEME_SUBJECT}>
      <DotDropDown title="" drop="end">
        <Dropdown.Item onClick={() => onView(data)}>
          <Eye className="dropdown-icon me-1" />
          View
        </Dropdown.Item>
        <Can I="update" a={THEME_SUBJECT}>
          <Dropdown.Item onClick={() => onEdit(data)}>
            <PencilSquare className="dropdown-icon me-1" />
            Edit
          </Dropdown.Item>
        </Can>
        <Can I="update" a={THEME_SUBJECT}>
          <Dropdown.Item onClick={() => onToggleActive(data)}>
            {data.isActive ? (
              <ToggleOff className="dropdown-icon me-1" />
            ) : (
              <ToggleOn className="dropdown-icon me-1" />
            )}
            {data.isActive ? 'Disable' : 'Enable'}
          </Dropdown.Item>
        </Can>
        <Can I="delete" a={THEME_SUBJECT}>
          <Dropdown.Item onClick={() => onDelete(data)}>
            <Trash className="dropdown-icon me-1" />
            Delete
          </Dropdown.Item>
        </Can>
      </DotDropDown>
    </CanMulti>
  );
};

export const GuestPortalThemesTable: FC = () => {
  const { setSimpleToasts } = useNotifications()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const propertyId = selectedProperty?.property.uuid ?? '';
  const themeService = useGuestPortalThemes();

  const [themes, setThemes] = useState<GuestThemeRecord[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<GuestPortalThemeModalMode>('create');
  const [activeRecord, setActiveRecord] = useState<GuestThemeRecord>();
  const [confirmModal, setConfirmModal] = useState<ModalState>({
    show: false,
    title: '',
    body: '',
    handler: () => {},
  });

  const loadThemes = useCallback(async () => {
    if (!propertyId) {
      setThemes([]);
      return;
    }
    try {
      setThemes(await themeService.list(propertyId));
    } catch {
      setSimpleToasts({ type: 'danger', message: 'Failed to load themes', show: true });
    }
  }, [propertyId, setSimpleToasts, themeService]);

  useEffect(() => {
    loadThemes();
  }, [loadThemes]);

  const onAdd = useCallback(() => {
    setActiveRecord(undefined);
    setModalMode('create');
    setShowModal(true);
  }, []);

  const onView = useCallback((record: GuestThemeRecord) => {
    setActiveRecord(record);
    setModalMode('view');
    setShowModal(true);
  }, []);

  const onEdit = useCallback((record: GuestThemeRecord) => {
    setActiveRecord(record);
    setModalMode('edit');
    setShowModal(true);
  }, []);

  const onToggleActive = useCallback(
    async (record: GuestThemeRecord) => {
      try {
        if (record.isActive) {
          await themeService.deactivate(record.id);
        } else {
          await themeService.activate(record.id);
        }
        await loadThemes();
      } catch {
        setSimpleToasts({
          type: 'danger',
          message: 'Failed to update theme status',
          show: true,
        });
      }
    },
    [loadThemes, setSimpleToasts, themeService],
  );

  const onDelete = useCallback(
    (record: GuestThemeRecord) => {
      setConfirmModal({
        show: true,
        title: 'Delete this theme?',
        body: 'This action cannot be undone.',
        handler: async () => {
          setConfirmModal((prev) => ({ ...prev, show: false }));
          await themeService.remove(record.id);
          await loadThemes();
          setSimpleToasts({ type: 'success', message: 'Theme deleted', show: true });
        },
      });
    },
    [loadThemes, setSimpleToasts, themeService],
  );

  const [columns] = useState<ColDef<GuestThemeRecord>[]>([
    { field: 'name', headerName: 'Theme name', flex: 3 },
    {
      headerName: 'Font',
      flex: 2,
      valueGetter: (params) => fontLabel(params.data?.theme.fontFamily ?? ''),
    },
    {
      headerName: 'Status',
      flex: 2,
      cellRenderer: (params: ICellRendererParams<GuestThemeRecord>) => (
        <StatusCell data={params.data} />
      ),
    },
    {
      headerName: 'ACTION',
      colId: 'action',
      cellClass: 'actionCol justify-content-end',
      flex: 1,
      cellRenderer: (params: ICellRendererParams<GuestThemeRecord>) => (
        <ThemeDotMenu
          data={params.data}
          onView={onView}
          onEdit={onEdit}
          onToggleActive={onToggleActive}
          onDelete={onDelete}
        />
      ),
    },
  ]);

  return (
    <>
      <div className="my-custom-table">
        <DataTable
          addButtonFunction={onAdd}
          addButtonText="Add theme"
          component={THEME_SUBJECT}
          refreshHandler={loadThemes}
          changeState={0}
          rowData={themes}
          columnDefs={columns}
          pagination
          paginationPageSize={config.tables.balances.paginationSize}
        />
      </div>

      <GuestPortalThemeModal
        show={showModal}
        mode={modalMode}
        propertyId={propertyId}
        data={activeRecord}
        handleClose={() => setShowModal(false)}
        onSaved={loadThemes}
      />

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        body={confirmModal.body}
        handleCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
        handleConfirm={() => confirmModal.handler?.()}
      />
    </>
  );
};
